// Pure utilities for matching a user's free-form chat input to AI Tools.
//
// Matching strategy (in priority order):
//   1. triggerPhrases — explicit phrases curated per tool (strongest signal)
//   2. Primary tool routing — one general-purpose tool per intent (e.g. image-generator)
//   3. Specialized tools — only when the query mentions their domain (logo, headshot…)
//   4. Whole-word token hits in name / meta.keywords (medium signal)
//
// Whole-word matching prevents false positives like "man" → "huMANizer".

const STOP_WORDS = new Set([
  'a', 'an', 'and', 'are', 'as', 'at', 'be', 'but', 'by', 'do', 'does', 'for',
  'from', 'has', 'have', 'how', 'i', 'in', 'is', 'it', 'me', 'my', 'of', 'on',
  'or', 'our', 'please', 'that', 'the', 'this', 'to', 'us', 'was', 'we', 'were',
  'what', 'when', 'where', 'will', 'with', 'you', 'your',
  'hey', 'hello', 'hi', 'whats', 'sup', 'good', 'thanks', 'thank', 'yeah', 'yes',
  'okay', 'ok', 'lol', 'haha', 'man', 'bro', 'dude', 'need', 'want',
]);

const DEFAULT_EXCLUDED_SLUGS = new Set(['powerpoint-generator']);

const FALLBACK_TRIGGER_PHRASES = require('./tool_trigger_phrases.json');

const MIN_QUERY_LENGTH = 6;
const MIN_TOKEN_LENGTH = 3;
const MAX_SUGGESTIONS = 1;
// When the user mentions images without a clear create vs edit verb, surface
// both the generator and editor (generator ranks first).
const MAX_AMBIGUOUS_IMAGE_SUGGESTIONS = 2;
const MIN_SCORE = 6;
const TRIGGER_PHRASE_SCORE = 30;

const WEIGHTS = {
  name: 5,
  keywords: 4,
  description: 2,
  tags: 1,
  multiNameBonus: 4,
  primaryToolBonus: 15,
  ambiguousImageSecondaryBonus: 8,
};

const CREATION_VERBS = new Set([
  'make', 'create', 'generate', 'draw', 'design', 'render', 'produce', 'paint', 'build',
]);

const EXTRACTION_VERB_PATTERN = 'extract|ocr|scan|parse|pull|convert|transcribe|decode';
const STRONG_IMAGE_NOUN_PATTERN = 'images?|pictures?|photos?|artwork|illustration|drawing|screenshot|selfie';
const CREATION_VERB_PATTERN = 'make|create|generate|draw|design|render|produce|paint|build';
const EDIT_VERB_PATTERN = 'edit|change|modify|fix|retouch|enhance|remove|erase|upscale|enlarge|sharpen|restore|inpaint';

// Backend-style modifiers between article and noun so "make a photorealistic
// image" matches, while "make a decision about the image" does not.
const IMAGE_MODIFIER_STOP = (
  'of|for|about|with|showing|depicting|featuring|the|a|an|my|our|this|that|and|or|to|in|on|at|'
  + 'from|into|as|by|vs|versus|decision|plan|choice|conclusion|report|summary|'
  + 'recommendation|suggestion|idea|question|answer'
);
const IMAGE_MODIFIER_TOKEN = `(?:(?!(?:${IMAGE_MODIFIER_STOP})\\b)[a-z][a-z0-9-]{1,24}\\s+)`;
const IMAGE_OBJECT_PREFIX = (
  `(?:me\\s+)?(?:an?\\s+|the\\s+|my\\s+|our\\s+)?(?:\\d{2,5}\\s*[x×]\\s*\\d{2,5}\\s+)?(?:${IMAGE_MODIFIER_TOKEN}){0,5}`
);
const IMAGE_CREATION_OBJECT = '(?:image|picture|photo|illustration|artwork|drawing|art)';

const EXPLICIT_IMAGE_CREATION_PATTERNS = [
  new RegExp(`\\b(?:${CREATION_VERB_PATTERN})\\s+${IMAGE_OBJECT_PREFIX}${IMAGE_CREATION_OBJECT}\\b`),
  new RegExp(
    `\\b(?:can|could|would|will)\\s+you\\s+(?:please\\s+)?(?:${CREATION_VERB_PATTERN})\\s+${IMAGE_OBJECT_PREFIX}${IMAGE_CREATION_OBJECT}\\b`
  ),
  new RegExp(
    `\\b(?:i\\s+)?(?:want|need|would\\s+like)(?:\\s+you\\s+to|\\s+to)?\\s+(?:${CREATION_VERB_PATTERN})\\s+${IMAGE_OBJECT_PREFIX}${IMAGE_CREATION_OBJECT}\\b`
  ),
];

const EXPLICIT_IMAGE_EDIT_PATTERNS = [
  /\b(?:edit|retouch|upscale|inpaint|enhance)\s+(?:the\s+|this\s+|my\s+|an?\s+)?(?:image|picture|photo|screenshot|artwork)\b/,
  /\b(?:remove|erase)\s+[\s\S]{0,40}\b(?:from\s+)?(?:this\s+|the\s+|my\s+)?(?:image|picture|photo|screenshot)\b/,
];

// Indefinite / want-need visual asks without a create verb: "an image of a dog".
const AMBIGUOUS_IMAGE_REQUEST_PATTERNS = [
  /\b(?:an?\s+)(?:\d{2,5}\s*[x×]\s*\d{2,5}\s+)?(?:image|picture|photo|illustration|artwork)\s+(?:of|for|showing|depicting|featuring)\b/,
  /\b(?:i\s+)?(?:want|need|would\s+like|looking\s+for)\s+(?:an?\s+)?(?:image|picture|photo|illustration|artwork)\b/,
  /\b(?:show\s+me|give\s+me)\s+(?:an?\s+)?(?:image|picture|photo|illustration|artwork)\b/,
];

const AMBIGUOUS_IMAGE_FALSE_POSITIVES = [
  /\b(?:here(?: s|s)|here is|attached|uploaded|enclosed)\s+(?:an?\s+|the\s+|my\s+)?(?:image|picture|photo|screenshot)\b/,
  /\b(?:what|who|where|why|how|which)\b[\s\S]{0,80}\b(?:this|the|my|that)\s+(?:image|picture|photo|screenshot)\b/,
  /\b(?:this|the|my|that)\s+(?:image|picture|photo|screenshot)\b[\s\S]{0,80}\b(?:what|who|where|why|how|which|mean|breed)\b/,
  /\b(?:tell me about|what (?:is|are)|history of|do you think about|opinion (?:on|of)|meaning of)\b[\s\S]{0,60}\b(?:image|picture|photo|art|artwork|illustration)\b/,
];

// Loose verb↔noun pairing is unsafe on long pastes (articles, surveys).
const VERB_NOUN_MAX_GAP = 80;
const MAX_IMAGE_PROXIMITY_QUERY_LENGTH = 800;

const MUSIC_NOUNS = new Set([
  'song', 'songs', 'music', 'track', 'tracks', 'beat', 'beats', 'melody', 'melodies',
  'instrumental', 'jingle', 'soundtrack', 'tune', 'tunes',
]);

const VIDEO_NOUNS = new Set([
  'video', 'videos', 'clip', 'clips', 'footage', 'movie', 'movies', 'film', 'films',
  'animation', 'animations', 'reel', 'reels',
]);

// Unambiguous 3D terms. "model/models/asset" alone often means spreadsheets,
// business plans, etc. — those require explicit 3d context (see is3DCreationQuery).
const THREE_D_NOUNS = new Set([
  '3d', 'mesh', 'meshes', 'glb', 'obj',
]);
const THREE_D_AMBIGUOUS_NOUNS = new Set([
  'model', 'models', 'asset', 'assets',
]);

const CODE_NOUNS = new Set([
  'code', 'coding', 'script', 'scripts', 'function', 'program', 'programming',
  'python', 'javascript', 'typescript', 'react', 'component', 'algorithm', 'api',
  'backend', 'frontend', 'sql', 'html', 'css', 'java', 'rust', 'golang', 'php',
]);

const MUSIC_CREATION_VERBS = new Set([
  ...CREATION_VERBS,
  'write', 'compose', 'sing',
]);

const CODING_VERBS = new Set([
  ...CREATION_VERBS,
  'write', 'implement', 'debug', 'fix',
]);

// The default tool to surface for broad intents. Specialized variants should
// only appear when the query mentions their domain keywords below.
const PRIMARY_TOOL_SLUGS = {
  'create-image': 'image-generator',
  'create-music': 'music-generator',
  'create-video': 'video-generator',
  'create-3d': 'text-to-3d',
  'create-vector': 'svg-generator',
  'edit-image': 'image-editor',
  'vectorize-image': 'image-vectorizer',
  coding: 'code-generator',
  research: 'research-assistant',
};

// Specialized create-image tools and the terms that justify suggesting them.
const SPECIALIZED_CREATE_IMAGE_KEYWORDS = {
  'logo-generator': ['logo', 'logos', 'brand', 'wordmark', 'startup', 'company', 'emblem', 'icon'],
  'photo-studio': ['headshot', 'portrait', 'product shot', 'product photo', 'studio shot', 'retouch', 'catalog'],
  'interior-design': ['interior', 'room', 'furniture', 'decorate', 'home design', 'living room', 'bedroom'],
};

const SPECIALIZED_EDIT_IMAGE_KEYWORDS = {
  'object-remover': [
    'remove object', 'remove person', 'remove the person', 'remove background', 'erase object',
    'inpaint', 'photo cleanup', 'remove from photo', 'person from photo', 'remove someone',
  ],
  'image-upscaler': ['upscale', 'upscaling', 'enlarge', '4k', 'sharper', 'resolution', 'enhance photo', 'photo enhancer', 'super resolution'],
  'clothes-changer': ['outfit', 'outfits', 'clothes', 'clothing', 'try on', 'wardrobe', 'wear', 'change outfit'],
};

const SPECIALIZED_IMAGE_TO_VIDEO_KEYWORDS = {
  'talking-photo': ['talking photo', 'animate photo', 'make photo talk', 'speaking photo'],
  'lipsync-generator': ['lip sync', 'lipsync', 'mouth sync', 'sync lips'],
};

// Vector tools produce SVG rather than raster output, so they should never be
// suggested for a plain image request — only when the user says so explicitly.
const SPECIALIZED_VECTOR_KEYWORDS = {
  'svg-generator': ['svg', 'vector', 'vectors', 'scalable'],
  'image-vectorizer': ['svg', 'vector', 'vectors', 'vectorize', 'vectorise', 'raster', 'trace'],
};

const SPECIALIZED_TOOL_KEYWORDS = {
  ...SPECIALIZED_CREATE_IMAGE_KEYWORDS,
  ...SPECIALIZED_EDIT_IMAGE_KEYWORDS,
  ...SPECIALIZED_IMAGE_TO_VIDEO_KEYWORDS,
  ...SPECIALIZED_VECTOR_KEYWORDS,
};

const VECTOR_NOUNS = new Set(['svg', 'vector', 'vectors']);

const VECTOR_CONVERSION_VERBS = new Set(['convert', 'trace', 'turn']);

const IMAGE_EXTRACTION_SLUGS = new Set(['image-to-text']);

const OCR_TRIGGER_PHRASES = [
  'extract text from image',
  'ocr this image',
  'read text in image',
  'scan text from photo',
  'convert image to text',
];

function escapeRegex(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function normalizeText(text) {
  return (text || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function tokenize(text) {
  return normalizeText(text)
    .split(/\s+/)
    .filter((token) => token.length >= MIN_TOKEN_LENGTH && !STOP_WORDS.has(token));
}

function fieldHasWord(field, token) {
  if (!field || !token) return false;
  const re = new RegExp(`\\b${escapeRegex(token)}\\b`, 'i');
  return re.test(String(field));
}

function getTagSlugs(tool) {
  if (!Array.isArray(tool?.tags)) return '';
  return tool.tags
    .map((tag) => (typeof tag === 'string' ? tag : tag?.slug) || '')
    .join(' ')
    .toLowerCase();
}

function getTriggerPhrases(tool) {
  const fromTool = Array.isArray(tool?.triggerPhrases)
    ? tool.triggerPhrases.filter((phrase) => typeof phrase === 'string' && phrase.trim())
    : [];
  if (fromTool.length) return fromTool;
  return FALLBACK_TRIGGER_PHRASES[tool?.slug] || [];
}

function stripImageIdioms(normalized) {
  return String(normalized || '')
    .replace(/\b(?:self|body|brand|public|mirror|mental|corporate|company)\s+images?\b/g, ' ')
    .replace(/\bbig picture\b/g, ' ')
    .replace(/\bmartial arts?\b/g, ' ')
    .replace(/\bstate of the art\b/g, ' ')
    .replace(/\bby design\b/g, ' ')
    .replace(/\bmake (?:sure|sense|clear|peace|use|fun|room|way)\b/g, ' ')
    .replace(/\bmake (?:a\s+)?(?:decision|plan|choice|difference|point|case|effort)\b/g, ' ')
    .replace(/\bcreate (?:monopolies|jobs|value|wealth|growth|opportunities)\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function hasVerbNounProximity(normalized, verbPattern, nounPattern, maxGap = VERB_NOUN_MAX_GAP) {
  if (!normalized) return false;
  const forward = new RegExp(`\\b(?:${verbPattern})\\b[\\s\\S]{0,${maxGap}}\\b(?:${nounPattern})\\b`);
  const reverse = new RegExp(`\\b(?:${nounPattern})\\b[\\s\\S]{0,${maxGap}}\\b(?:${verbPattern})\\b`);
  return forward.test(normalized) || reverse.test(normalized);
}

function hasExplicitImageCreationPhrase(normalized) {
  return EXPLICIT_IMAGE_CREATION_PATTERNS.some((pattern) => pattern.test(normalized));
}

function isImageCreationQuery(tokens, query) {
  const normalized = stripImageIdioms(normalizeText(query));
  if (hasExplicitImageCreationPhrase(normalized)) return true;
  // Distant "create monopolies" + "abstract art" must not count. Only pair a
  // creation verb with a visual noun when they actually sit near each other,
  // and only on short prompts where that pairing is unlikely to be incidental.
  if ((query || '').length > MAX_IMAGE_PROXIMITY_QUERY_LENGTH) return false;
  return hasVerbNounProximity(normalized, CREATION_VERB_PATTERN, STRONG_IMAGE_NOUN_PATTERN)
    || hasVerbNounProximity(normalized, CREATION_VERB_PATTERN, 'art');
}

function isImageEditQuery(tokens, query) {
  const normalized = stripImageIdioms(normalizeText(query));
  // Named visual-attribute edits ("change the wood background", typo'd
  // "bsckground") are edit requests even without an explicit image noun —
  // the attached/recent image is the implied subject.
  const hasAttributeEdit = /\b(change|replace|swap|remove|edit|modify|alter|adjust|update)\s+(the\s+|my\s+|this\s+)?([a-z][a-z0-9-]{0,19}\s+){0,2}(b[a-z]{0,3}ckgrounds?|back\s*grounds?|bgs?|colo(?:u)?rs?|styles?|sky|lighting|watermark|border|shadow)\b/.test(normalized);
  if (hasAttributeEdit) return true;
  if (EXPLICIT_IMAGE_EDIT_PATTERNS.some((pattern) => pattern.test(normalized))) return true;
  if ((query || '').length > MAX_IMAGE_PROXIMITY_QUERY_LENGTH) return false;
  return hasVerbNounProximity(normalized, EDIT_VERB_PATTERN, STRONG_IMAGE_NOUN_PATTERN);
}

// Mentions images/photos in a request-shaped way but without a create/edit/OCR
// verb. Example: "an image of a dog", "I need an image for my blog".
// Bare topical mentions ("abstract art", "the big picture") do not count.
function isAmbiguousImageQuery(tokens, query) {
  if ((query || '').length > MAX_IMAGE_PROXIMITY_QUERY_LENGTH) return false;
  if (isImageCreationQuery(tokens, query)) return false;
  if (isImageEditQuery(tokens, query)) return false;
  if (isImageExtractionQuery(tokens, query)) return false;

  const normalized = stripImageIdioms(normalizeText(query));
  if (!normalized) return false;
  if (AMBIGUOUS_IMAGE_FALSE_POSITIVES.some((pattern) => pattern.test(normalized))) return false;
  return AMBIGUOUS_IMAGE_REQUEST_PATTERNS.some((pattern) => pattern.test(normalized));
}

function hasVectorNoun(tokens) {
  return tokens.some((token) => VECTOR_NOUNS.has(token));
}

// The user already has artwork and wants it traced: "vectorize this logo",
// "png to svg", "convert it to a vector".
function isVectorConversionQuery(tokens, query) {
  if (tokens.some((token) => token.startsWith('vectoriz') || token.startsWith('vectoris'))) {
    return true;
  }
  if (!hasVectorNoun(tokens)) return false;

  const normalized = normalizeText(query);
  return tokens.some((token) => VECTOR_CONVERSION_VERBS.has(token))
    || /\b(png|jpg|jpeg|webp|image|photo|picture|logo|icon|raster|screenshot)\s+(in)?to\s+(an?\s+)?(svg|vector)\b/.test(normalized);
}

// Any request whose output has to be SVG. Bare mentions ("what is an svg")
// stay out — the term needs a creation verb, a want, or a concrete artifact.
function isVectorRequestQuery(tokens, query) {
  if (isVectorConversionQuery(tokens, query)) return true;
  if (!hasVectorNoun(tokens)) return false;

  const normalized = normalizeText(query);
  return tokens.some((token) => CREATION_VERBS.has(token))
    || /\b(want|need|looking for)\b/.test(normalized)
    || /\b(svg|vector)\s+(icon|icons|art|artwork|illustration|illustrations|graphic|graphics|logo|file|files|version)\b/.test(normalized);
}

// Vectorizer first when there is source art to trace, generator first when the
// user is describing something that does not exist yet.
function getVectorToolSlugs(tokens, query) {
  if (!isVectorRequestQuery(tokens, query)) return [];
  return isVectorConversionQuery(tokens, query)
    ? [PRIMARY_TOOL_SLUGS['vectorize-image'], PRIMARY_TOOL_SLUGS['create-vector']]
    : [PRIMARY_TOOL_SLUGS['create-vector'], PRIMARY_TOOL_SLUGS['vectorize-image']];
}

function isMusicCreationQuery(tokens, query) {
  const normalized = normalizeText(query);
  const hasCreationVerb = tokens.some((token) => MUSIC_CREATION_VERBS.has(token))
    || /\b(make|create|generate|write|compose|produce)\s+(an?\s+)?(song|track|beat|music|melody|tune)\b/.test(normalized);
  const hasMusicNoun = tokens.some((token) => MUSIC_NOUNS.has(token))
    || /\b(an?\s+)?(song|track|beat|music|melody|tune|instrumental)\b/.test(normalized);
  return hasCreationVerb && hasMusicNoun;
}

function isVideoCreationQuery(tokens, query) {
  const normalized = normalizeText(query);
  const hasCreationVerb = tokens.some((token) => CREATION_VERBS.has(token))
    || /\b(make|create|generate|produce|film)\s+(an?\s+)?(video|clip|movie|film|reel|animation)\b/.test(normalized);
  const hasVideoNoun = tokens.some((token) => VIDEO_NOUNS.has(token))
    || /\b(an?\s+)?(video|clip|movie|film|reel|animation)\b/.test(normalized);
  return hasCreationVerb && hasVideoNoun;
}

function has3DSignal(tokens, query) {
  const normalized = normalizeText(query);
  if (/\b(3d model|text to 3d|3d asset|3d mesh|glb model|3d printable)\b/.test(normalized)) {
    return true;
  }
  if (tokens.some((token) => THREE_D_NOUNS.has(token))) {
    return true;
  }
  if (tokens.some((token) => THREE_D_AMBIGUOUS_NOUNS.has(token))) {
    return normalized.includes('3d')
      || tokens.includes('3d')
      || /\b3d\b/.test(normalized);
  }
  return false;
}

function is3DCreationQuery(tokens, query) {
  const normalized = normalizeText(query);
  const hasCreationVerb = tokens.some((token) => CREATION_VERBS.has(token))
    || /\b(make|create|generate|build)\s+(an?\s+)?(3d(\s+(model|mesh|asset))?|mesh|glb)\b/.test(normalized);
  return hasCreationVerb && has3DSignal(tokens, query);
}

function isCodingQuery(tokens, query) {
  const normalized = normalizeText(query);
  const hasCodeNoun = tokens.some((token) => CODE_NOUNS.has(token))
    || /\b(code|coding|script|function|program|python|javascript|typescript|react component)\b/.test(normalized);
  const hasCodingVerb = tokens.some((token) => CODING_VERBS.has(token))
    || /\b(write|generate|create|implement|build|debug|fix)\s+(some\s+)?(code|a script|a function|python|javascript|typescript|react)\b/.test(normalized);
  return hasCodeNoun && hasCodingVerb;
}

function isResearchQuery(tokens, query) {
  const normalized = normalizeText(query);
  return tokens.some((token) => ['research', 'investigate', 'explore', 'study'].includes(token))
    || /\b(research|deep dive|find sources|literature review|learn about)\b/.test(normalized);
}

function isTextToSpeechQuery(tokens, query) {
  const normalized = normalizeText(query);
  const speechTerms = ['aloud', 'speech', 'voiceover', 'narration', 'voice', 'tts', 'narrator'];
  const hasSpeechTerm = tokens.some((token) => speechTerms.includes(token))
    || /\b(read aloud|text to speech|text to voice|speak this|voiceover)\b/.test(normalized);
  const hasTextTarget = tokens.includes('text')
    || /\b(this text|my text|paragraph|article|script|essay)\b/.test(normalized);
  return hasSpeechTerm && hasTextTarget;
}

function isImageExtractionQuery(tokens, query) {
  if (scoreTriggerPhrases(query, OCR_TRIGGER_PHRASES) > 0) return true;
  const normalized = stripImageIdioms(normalizeText(query));
  return hasVerbNounProximity(normalized, EXTRACTION_VERB_PATTERN, STRONG_IMAGE_NOUN_PATTERN);
}

function matchesSpecializedTool(tool, tokens, query) {
  const keywords = SPECIALIZED_TOOL_KEYWORDS[tool?.slug];
  if (!keywords) return true;

  const normalized = normalizeText(query);
  for (const keyword of keywords) {
    const normalizedKeyword = normalizeText(keyword);
    if (normalizedKeyword.includes(' ')) {
      if (normalized.includes(normalizedKeyword)) return true;
      continue;
    }
    if (tokens.includes(normalizedKeyword)) return true;
    if (fieldHasWord(normalized, normalizedKeyword)) return true;
  }
  return false;
}

function isPrimaryTool(tool) {
  return PRIMARY_TOOL_SLUGS[tool?.intentAction] === tool?.slug;
}

function hasPrimaryToolInCatalog(catalog, intentAction, excludedSlugs) {
  const slug = PRIMARY_TOOL_SLUGS[intentAction];
  if (!slug) return false;
  return catalog.some((tool) => tool.slug === slug && isToolEligible(tool, excludedSlugs));
}

function hasSpecializedEditMatch(tokens, query) {
  for (const slug of Object.keys(SPECIALIZED_EDIT_IMAGE_KEYWORDS)) {
    if (matchesSpecializedTool({ slug }, tokens, query)) return true;
  }
  return false;
}

function shouldSuppressTool(tool, tokens, query) {
  if (IMAGE_EXTRACTION_SLUGS.has(tool?.slug)) {
    if (isImageExtractionQuery(tokens, query)) return false;
    return true;
  }

  if (SPECIALIZED_VECTOR_KEYWORDS[tool?.slug] && !matchesSpecializedTool(tool, tokens, query)) {
    return true;
  }

  if (SPECIALIZED_CREATE_IMAGE_KEYWORDS[tool?.slug] && isImageCreationQuery(tokens, query)) {
    return !matchesSpecializedTool(tool, tokens, query);
  }

  if (SPECIALIZED_EDIT_IMAGE_KEYWORDS[tool?.slug] && isImageEditQuery(tokens, query)) {
    return !matchesSpecializedTool(tool, tokens, query);
  }

  if (SPECIALIZED_IMAGE_TO_VIDEO_KEYWORDS[tool?.slug] && isVideoCreationQuery(tokens, query)) {
    return !matchesSpecializedTool(tool, tokens, query);
  }

  if (tool?.slug === 'video-generator' && isCodingQuery(tokens, query) && !isVideoCreationQuery(tokens, query)) {
    return true;
  }

  if (tool?.slug === 'text-to-3d' && isTextToSpeechQuery(tokens, query)) {
    return true;
  }

  if (tool?.slug === 'text-humanizer' && isTextToSpeechQuery(tokens, query)) {
    return true;
  }

  return false;
}

function scoreTriggerPhrases(query, phrases) {
  const normalizedQuery = normalizeText(query);
  if (!normalizedQuery) return 0;

  let best = 0;
  for (const phrase of phrases) {
    const normalizedPhrase = normalizeText(phrase);
    if (normalizedPhrase.length < 6) continue;

    const phraseWords = normalizedPhrase.split(' ').filter((word) => word.length >= 3);
    if (phraseWords.length < 2 && normalizedPhrase.length < 10) continue;
    if (!normalizedQuery.includes(normalizedPhrase)) continue;

    best = Math.max(best, TRIGGER_PHRASE_SCORE + Math.min(normalizedPhrase.length, 20));
  }
  return best;
}

function scoreTool(tool, tokens, query) {
  if (shouldSuppressTool(tool, tokens, query)) return 0;

  const triggerScore = scoreTriggerPhrases(query, getTriggerPhrases(tool));
  if (triggerScore > 0) return triggerScore;

  if (tool.intentAction === 'create-3d' && !has3DSignal(tokens, query)) {
    return 0;
  }

  const ambiguousImage = isAmbiguousImageQuery(tokens, query);
  const genericImageCreation = isImageCreationQuery(tokens, query);
  const genericImageEdit = isImageEditQuery(tokens, query);

  // Generic image generator/editor must not score on incidental tokens like
  // "art" in "abstract art" or "image" in "body image".
  if (
    (tool.slug === PRIMARY_TOOL_SLUGS['create-image'] || tool.slug === PRIMARY_TOOL_SLUGS['edit-image'])
    && !genericImageCreation
    && !genericImageEdit
    && !ambiguousImage
  ) {
    return 0;
  }

  const name = tool.name || '';
  const desc = tool.description || tool.shortDescription || '';
  const keywords = tool.meta?.keywords || '';
  const tags = getTagSlugs(tool);

  let score = 0;
  let nameHits = 0;
  let keywordHits = 0;

  for (const token of tokens) {
    if (fieldHasWord(name, token)) {
      score += WEIGHTS.name;
      nameHits += 1;
    }
    if (fieldHasWord(keywords, token)) {
      score += WEIGHTS.keywords;
      keywordHits += 1;
    }
    if (fieldHasWord(desc, token)) {
      score += WEIGHTS.description;
    }
    if (fieldHasWord(tags, token)) {
      score += WEIGHTS.tags;
    }
  }

  if (nameHits >= 2) score += WEIGHTS.multiNameBonus;

  const hasStrongHit = nameHits > 0 || keywordHits > 0;
  if (ambiguousImage) {
    if (tool.slug === PRIMARY_TOOL_SLUGS['create-image']) {
      score += WEIGHTS.primaryToolBonus;
      if (!hasStrongHit) score += WEIGHTS.primaryToolBonus;
      return score;
    }
    if (tool.slug === PRIMARY_TOOL_SLUGS['edit-image']) {
      score += WEIGHTS.ambiguousImageSecondaryBonus;
      return score;
    }
    if (SPECIALIZED_CREATE_IMAGE_KEYWORDS[tool?.slug] || SPECIALIZED_EDIT_IMAGE_KEYWORDS[tool?.slug]) {
      return matchesSpecializedTool(tool, tokens, query) ? score : 0;
    }
    if (tool.outputType === 'image' || tool.intentAction === 'create-image' || tool.intentAction === 'edit-image') {
      return 0;
    }
  }

  const genericMusicCreation = isMusicCreationQuery(tokens, query);
  const genericVideoCreation = isVideoCreationQuery(tokens, query);
  const generic3DCreation = is3DCreationQuery(tokens, query);
  const genericCoding = isCodingQuery(tokens, query);
  const genericResearch = isResearchQuery(tokens, query);

  if (genericImageCreation && tool.intentAction === 'create-image' && tool.outputType === 'image') {
    if (isPrimaryTool(tool)) {
      score += WEIGHTS.primaryToolBonus;
      if (!hasStrongHit) score += WEIGHTS.primaryToolBonus;
    } else if (hasStrongHit && matchesSpecializedTool(tool, tokens, query)) {
      score += 6;
    } else {
      return 0;
    }
  } else if (genericMusicCreation && tool.intentAction === 'create-music') {
    if (isPrimaryTool(tool)) {
      score += WEIGHTS.primaryToolBonus;
      if (!hasStrongHit) score += WEIGHTS.primaryToolBonus;
    } else if (!hasStrongHit) {
      return 0;
    }
  } else if (genericVideoCreation && tool.intentAction === 'create-video') {
    if (isPrimaryTool(tool)) {
      score += WEIGHTS.primaryToolBonus;
      if (!hasStrongHit) score += WEIGHTS.primaryToolBonus;
    } else if (!hasStrongHit) {
      return 0;
    }
  } else if (generic3DCreation && tool.intentAction === 'create-3d') {
    if (isPrimaryTool(tool)) {
      score += WEIGHTS.primaryToolBonus;
      if (!hasStrongHit) score += WEIGHTS.primaryToolBonus;
    } else if (!hasStrongHit) {
      return 0;
    }
  } else if (genericImageEdit && tool.intentAction === 'edit-image' && tool.outputType === 'image') {
    const specializedMatch = SPECIALIZED_EDIT_IMAGE_KEYWORDS[tool?.slug]
      && matchesSpecializedTool(tool, tokens, query);
    if (specializedMatch) {
      score += WEIGHTS.primaryToolBonus;
      if (!hasStrongHit) score += WEIGHTS.primaryToolBonus;
    } else if (isPrimaryTool(tool)) {
      if (hasSpecializedEditMatch(tokens, query)) return 0;
      score += WEIGHTS.primaryToolBonus;
      if (!hasStrongHit) score += WEIGHTS.primaryToolBonus;
    } else {
      return 0;
    }
  } else if (genericCoding && tool.intentAction === 'coding') {
    if (isPrimaryTool(tool)) {
      score += WEIGHTS.primaryToolBonus;
      if (!hasStrongHit) score += WEIGHTS.primaryToolBonus;
    } else if (!hasStrongHit) {
      return 0;
    }
  } else if (genericResearch && tool.intentAction === 'research') {
    if (isPrimaryTool(tool)) {
      score += WEIGHTS.primaryToolBonus;
      if (!hasStrongHit) score += WEIGHTS.primaryToolBonus;
    } else if (!hasStrongHit) {
      return 0;
    }
  } else if (
    SPECIALIZED_IMAGE_TO_VIDEO_KEYWORDS[tool?.slug]
    && (tool.intentAction === 'image-to-video' || SPECIALIZED_IMAGE_TO_VIDEO_KEYWORDS[tool?.slug])
    && !matchesSpecializedTool(tool, tokens, query)
    && !hasStrongHit
  ) {
    return 0;
  } else if (!hasStrongHit) {
    return 0;
  }

  return score;
}

function shouldSkipQuery(query) {
  const trimmed = (query || '').trim();
  if (trimmed.length < MIN_QUERY_LENGTH) return true;
  if (trimmed.startsWith('/')) return true;
  return false;
}

function isToolEligible(tool, excludedSlugs) {
  if (!tool?.slug) return false;
  if (excludedSlugs.has(tool.slug)) return false;
  if (tool.flags?.isEnabled === false) return false;
  if (tool.status && tool.status !== 'active') return false;
  return true;
}

function shouldSkipSpecializedForPrimary(catalog, excludedSlugs, intentAction, isGenericQuery, tool, tokens, query, keywordMap) {
  if (!isGenericQuery) return false;
  if (!keywordMap[tool.slug]) return false;
  if (!hasPrimaryToolInCatalog(catalog, intentAction, excludedSlugs)) return false;
  return !matchesSpecializedTool(tool, tokens, query);
}

function isImageRequestQuery(query) {
  if (shouldSkipQuery(query)) return false;
  const tokens = tokenize(query);
  return isImageCreationQuery(tokens, query)
    || isImageEditQuery(tokens, query)
    || isVectorRequestQuery(tokens, query)
    || isAmbiguousImageQuery(tokens, query);
}

function getRecommendedImageTools(query, tools, options = {}) {
  if (!isImageRequestQuery(query)) return [];

  const excludedSlugs = options.excludedSlugs ?? DEFAULT_EXCLUDED_SLUGS;
  const catalog = tools || [];
  const vectorSlugs = getVectorToolSlugs(tokenize(query), query);
  const slugs = vectorSlugs.length
    ? vectorSlugs
    : [PRIMARY_TOOL_SLUGS['create-image'], PRIMARY_TOOL_SLUGS['edit-image']];
  const recommended = [];

  for (const slug of slugs) {
    const tool = catalog.find((entry) => entry?.slug === slug && isToolEligible(entry, excludedSlugs));
    if (tool) recommended.push(tool);
  }

  if (recommended.length) return recommended;

  return matchToolsForIntent(query, catalog, {
    ...options,
    maxResults: MAX_AMBIGUOUS_IMAGE_SUGGESTIONS,
  });
}

function getMessageText(message) {
  if (!message) return '';
  return typeof message.content === 'string' ? message.content : String(message.content || '');
}

function findUserMessageForAssistant(assistantMessage, messages) {
  if (!assistantMessage || !Array.isArray(messages) || !messages.length) return null;

  if (assistantMessage.parentMessageId) {
    const parent = messages.find(
      (msg) => msg?.role === 'user'
        && (msg.id === assistantMessage.parentMessageId || msg._id === assistantMessage.parentMessageId)
    );
    if (parent) return parent;
  }

  const assistantIndex = messages.findIndex(
    (msg) => msg === assistantMessage
      || (assistantMessage.id && (msg.id === assistantMessage.id || msg._id === assistantMessage.id))
      || (assistantMessage._id && (msg.id === assistantMessage._id || msg._id === assistantMessage._id))
  );

  if (assistantIndex > 0) {
    for (let i = assistantIndex - 1; i >= 0; i -= 1) {
      if (messages[i]?.role === 'user') return messages[i];
    }
  }

  return null;
}

// Resolve the user prompt that triggered an assistant reply. This is
// intentionally independent of backend intent routing — if Auto mode
// misclassified an image request as "answer", we still suggest image tools.
function resolveImageRequestUserQuery(assistantMessage, messages) {
  if (assistantMessage?.role !== 'assistant') return null;

  const userMessage = findUserMessageForAssistant(assistantMessage, messages);
  const userQuery = getMessageText(userMessage);
  if (!userQuery || !isImageRequestQuery(userQuery)) return null;
  return userQuery;
}

function resolveImageRequestUserQueryForGroup(group, messages) {
  if (!group?.messages?.length) return null;

  for (const msg of group.messages) {
    const query = resolveImageRequestUserQuery(msg, messages);
    if (query) return query;
  }

  if (group.parentMessageId) {
    const parent = messages.find(
      (msg) => msg?.role === 'user'
        && (msg.id === group.parentMessageId || msg._id === group.parentMessageId)
    );
    const userQuery = getMessageText(parent);
    if (userQuery && isImageRequestQuery(userQuery)) return userQuery;
  }

  return null;
}

function buildAssistantImageRequestMap(messages) {
  const map = new Map();
  if (!Array.isArray(messages) || !messages.length) return map;

  for (const msg of messages) {
    if (msg?.role !== 'assistant') continue;
    const userQuery = resolveImageRequestUserQuery(msg, messages);
    if (!userQuery) continue;
    if (msg.id) map.set(msg.id, userQuery);
    if (msg._id) map.set(String(msg._id), userQuery);
  }

  return map;
}

function matchToolsForIntent(query, tools, options = {}) {
  const excludedSlugs = options.excludedSlugs ?? DEFAULT_EXCLUDED_SLUGS;
  const maxResults = options.maxResults ?? MAX_SUGGESTIONS;
  const minScore = options.minScore ?? MIN_SCORE;

  if (shouldSkipQuery(query)) return [];

  const tokens = tokenize(query);
  const ambiguousImage = isAmbiguousImageQuery(tokens, query);
  const effectiveMaxResults = ambiguousImage
    ? Math.max(maxResults, MAX_AMBIGUOUS_IMAGE_SUGGESTIONS)
    : maxResults;
  const catalog = tools || [];
  const scored = [];

  for (const tool of catalog) {
    if (!isToolEligible(tool, excludedSlugs)) continue;
    if (shouldSuppressTool(tool, tokens, query)) continue;

    if (shouldSkipSpecializedForPrimary(
      catalog,
      excludedSlugs,
      'create-image',
      isImageCreationQuery(tokens, query),
      tool,
      tokens,
      query,
      SPECIALIZED_CREATE_IMAGE_KEYWORDS
    )) {
      continue;
    }

    if (shouldSkipSpecializedForPrimary(
      catalog,
      excludedSlugs,
      'edit-image',
      isImageEditQuery(tokens, query),
      tool,
      tokens,
      query,
      SPECIALIZED_EDIT_IMAGE_KEYWORDS
    )) {
      continue;
    }

    if (shouldSkipSpecializedForPrimary(
      catalog,
      excludedSlugs,
      'create-video',
      isVideoCreationQuery(tokens, query),
      tool,
      tokens,
      query,
      SPECIALIZED_IMAGE_TO_VIDEO_KEYWORDS
    )) {
      continue;
    }

    const score = scoreTool(tool, tokens, query);
    if (score >= minScore) scored.push({ tool, score });
  }

  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, effectiveMaxResults).map((entry) => entry.tool);
}

module.exports = {
  matchToolsForIntent,
  tokenize,
  scoreTool,
  scoreTriggerPhrases,
  shouldSkipQuery,
  isToolEligible,
  shouldSuppressTool,
  matchesSpecializedTool,
  isImageCreationQuery,
  isImageEditQuery,
  isAmbiguousImageQuery,
  isImageRequestQuery,
  getRecommendedImageTools,
  getMessageText,
  resolveImageRequestUserQuery,
  resolveImageRequestUserQueryForGroup,
  buildAssistantImageRequestMap,
  isMusicCreationQuery,
  isVideoCreationQuery,
  is3DCreationQuery,
  isVectorRequestQuery,
  isVectorConversionQuery,
  isCodingQuery,
  isResearchQuery,
  fieldHasWord,
  normalizeText,
  STOP_WORDS,
  DEFAULT_EXCLUDED_SLUGS,
  PRIMARY_TOOL_SLUGS,
  SPECIALIZED_TOOL_KEYWORDS,
  SPECIALIZED_CREATE_IMAGE_KEYWORDS,
  SPECIALIZED_EDIT_IMAGE_KEYWORDS,
  SPECIALIZED_IMAGE_TO_VIDEO_KEYWORDS,
  SPECIALIZED_VECTOR_KEYWORDS,
  MIN_QUERY_LENGTH,
  MIN_TOKEN_LENGTH,
  MAX_SUGGESTIONS,
  MAX_AMBIGUOUS_IMAGE_SUGGESTIONS,
  MIN_SCORE,
  WEIGHTS,
};
